/**
 * `CurveThumb` (W1-12 ui-kit-i18n; contract CurveThumbProps, DESIGN.md §4.12): an easing thumbnail drawn as an
 * SVG polyline sampled from `Ease.modelY` (Phira's formulas, so 23 / 29 show Phira's shape, not RPE's).
 *  - "Stay, then jump" draws a step at the end; a slice draws only the used part, renormalised as Phira does.
 *  - `showOvershoot`: the box grows to include values outside [0, 1] and shades that area; otherwise the
 *    curve (and the hover dot) is clipped to the unit box, widened by half a stroke so the ends at (0, 0) and
 *    (1, 1) keep their round caps.
 *  - `animate` (default true): on hover a dot travels along the curve, unless reduced motion is on.
 *  - A slice Phira cannot evaluate (f(left) = f(right): NaN) draws a dashed empty box and says so.
 *  - role="img" with a name ("OutQuad (RPE 4)", "Stay, then jump", "Custom curve (Bezier)").
 */

import { useEffect, useId, useMemo, useState } from 'react';
import type { Ease } from '../core/ease/types';
import { useT } from '../i18n/useT';
import { useUiCore, type UiCoreApis } from './coreApis';
import s from './CurveThumb.module.css';
import { cx } from './internal/cx';
import { formatFieldNumber } from './internal/numbers';
import { useReducedMotion } from './internal/useReducedMotion';
import type { CurveThumbProps } from './types';
import { m } from './ui.messages';

const SAMPLES = 48;
const LOOP_MS = 1400;
const HOLD_END_MS = 350;
/** Half the curve stroke (CurveThumb.module.css): the clip box is widened by it so line caps are not cut. */
const CLIP_MARGIN = 1;

export interface CurveSamples {
  /** [x, y] pairs in curve space (x 0..1, y unbounded); null when the curve cannot be evaluated. */
  readonly points: ReadonlyArray<readonly [number, number]> | null;
  readonly yMin: number;
  readonly yMax: number;
}

/** Samples a model ease for drawing. Hold is a step (0 until the end, then 1). */
export function sampleCurve(ease: Ease, modelY: UiCoreApis['modelY'], samples = SAMPLES): CurveSamples {
  if (ease.kind === 'hold') {
    return {
      points: [
        [0, 0],
        [1, 0],
        [1, 1],
      ],
      yMin: 0,
      yMax: 1,
    };
  }
  const points: Array<readonly [number, number]> = [];
  let yMin = 0;
  let yMax = 1;
  for (let i = 0; i <= samples; i++) {
    const x = i / samples;
    const y = modelY(ease, x);
    if (!Number.isFinite(y)) return { points: null, yMin: 0, yMax: 1 };
    yMin = Math.min(yMin, y);
    yMax = Math.max(yMax, y);
    points.push([x, y]);
  }
  return { points, yMin, yMax };
}

/** y on the sampled polyline at x (linear between samples). */
function yAt(points: ReadonlyArray<readonly [number, number]>, x: number): number {
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1];
    const b = points[i];
    if (a === undefined || b === undefined) break;
    if (x <= b[0]) {
      const span = b[0] - a[0];
      return span <= 0 ? b[1] : a[1] + ((x - a[0]) / span) * (b[1] - a[1]);
    }
  }
  return points[points.length - 1]?.[1] ?? 0;
}

function useCurveName(ease: Ease, easeName: UiCoreApis['easeName']): string {
  const t = useT();
  switch (ease.kind) {
    case 'hold':
      return t(m['curve.hold']);
    case 'bezier':
      return t(m['curve.bezier']);
    case 'tween': {
      const base =
        ease.id === 1
          ? t(m['curve.linear'])
          : t(m['curve.tween'], { id: ease.id, name: easeName(ease.id) ?? t(m['curve.label']) });
      const whole = ease.left <= 0 && ease.right >= 1;
      return whole
        ? base
        : t(m['curve.slice'], {
            name: base,
            left: formatFieldNumber(ease.left, 2),
            right: formatFieldNumber(ease.right, 2),
          });
    }
  }
}

export function CurveThumb(props: CurveThumbProps & { readonly className?: string | undefined }) {
  const t = useT();
  const { modelY, easeName } = useUiCore();
  const reduced = useReducedMotion();
  const size = props.size ?? 32;
  const animate = props.animate ?? true;
  const [hover, setHover] = useState(false);
  const [phase, setPhase] = useState<number | null>(null);
  const clipId = `curve-clip-${useId().replace(/[^\w-]/g, '')}`;
  const name = useCurveName(props.ease, easeName);
  const { ease } = props;
  const sampled = useMemo(() => sampleCurve(ease, modelY), [ease, modelY]);

  const pad = Math.max(2, size * 0.12);
  const inner = size - 2 * pad;
  const yMin = props.showOvershoot === true ? sampled.yMin : 0;
  const yMax = props.showOvershoot === true ? sampled.yMax : 1;
  const px = (x: number): number => pad + x * inner;
  const py = (y: number): number => pad + ((yMax - y) / (yMax - yMin)) * inner;

  const clip = props.showOvershoot !== true;
  const playing = animate && hover && !reduced && sampled.points !== null;
  useEffect(() => {
    if (!playing) return;
    let raf = 0;
    const start = performance.now();
    const tick = (now: number): void => {
      const elapsed = (now - start) % (LOOP_MS + HOLD_END_MS);
      setPhase(Math.min(1, elapsed / LOOP_MS));
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      setPhase(null);
    };
  }, [playing]);

  const points = sampled.points;
  const polyline = points?.map(([x, y]) => `${px(x).toFixed(2)},${py(y).toFixed(2)}`).join(' ') ?? '';
  const label = points === null ? `${name}: ${t(m['curve.invalid'])}` : name;
  const dot = playing && phase !== null && points !== null ? [px(phase), py(yAt(points, phase))] : null;

  return (
    <svg
      className={cx(s.thumb, props.className)}
      width={size}
      height={size}
      viewBox={`0 0 ${String(size)} ${String(size)}`}
      role="img"
      aria-label={label}
      data-ease={props.ease.kind}
      onPointerEnter={() => {
        setHover(true);
      }}
      onPointerLeave={() => {
        setHover(false);
      }}
    >
      {props.showOvershoot === true && yMax > 1 && (
        <rect className={s.overshoot} x={pad} y={py(yMax)} width={inner} height={py(1) - py(yMax)} />
      )}
      {props.showOvershoot === true && yMin < 0 && (
        <rect className={s.overshoot} x={pad} y={py(0)} width={inner} height={py(yMin) - py(0)} />
      )}
      <rect
        className={cx(s.frame, points === null && s.invalid)}
        x={pad}
        y={py(1)}
        width={inner}
        height={py(0) - py(1)}
      />
      {clip && (
        <defs>
          <clipPath id={clipId}>
            <rect
              x={pad - CLIP_MARGIN}
              y={py(1) - CLIP_MARGIN}
              width={inner + 2 * CLIP_MARGIN}
              height={py(0) - py(1) + 2 * CLIP_MARGIN}
            />
          </clipPath>
        </defs>
      )}
      <g clipPath={clip ? `url(#${clipId})` : undefined}>
        {points !== null && <polyline className={s.curve} points={polyline} />}
        {dot !== null && <circle className={s.dot} cx={dot[0]} cy={dot[1]} r={Math.max(1.5, size * 0.06)} />}
      </g>
    </svg>
  );
}
